import { useCallback, useEffect, useRef, useState } from 'react'
import type { RawMessage } from '@sensor/shared'
import { clearMessages, fetchMessages } from './api.js'

export type ConnectionState = 'connecting' | 'live' | 'offline'

export interface LiveStats {
  total: number
  buffered: number
  lastId: number
}

/** Tope de mensajes que el navegador mantiene en memoria. */
const MAX_CLIENT_MESSAGES = 500

/**
 * Mantiene la lista de mensajes al día vía SSE.
 *
 * El EventSource reconecta solo si se cae la red; en cada reconexión llega el
 * evento `hello` y se piden por HTTP los mensajes posteriores al último id
 * conocido, para no perder lo que llegó mientras estábamos desconectados.
 */
export function useLiveMessages() {
  const [messages, setMessages] = useState<RawMessage[]>([])
  const [stats, setStats] = useState<LiveStats>({ total: 0, buffered: 0, lastId: 0 })
  const [connection, setConnection] = useState<ConnectionState>('connecting')
  const [error, setError] = useState<string | null>(null)

  // Ref además del estado: los callbacks del EventSource no ven el estado nuevo.
  const lastIdRef = useRef(0)

  const mergeMessages = useCallback((incoming: RawMessage[]) => {
    if (incoming.length === 0) return
    setMessages((current) => {
      const seen = new Set(current.map((m) => m.id))
      const fresh = incoming.filter((m) => !seen.has(m.id))
      if (fresh.length === 0) return current
      // La lista se muestra del más nuevo al más viejo.
      const merged = [...fresh, ...current].sort((a, b) => b.id - a.id)
      return merged.slice(0, MAX_CLIENT_MESSAGES)
    })
    for (const message of incoming) {
      if (message.id > lastIdRef.current) lastIdRef.current = message.id
    }
  }, [])

  const catchUp = useCallback(async () => {
    try {
      const data = await fetchMessages(lastIdRef.current, MAX_CLIENT_MESSAGES)
      mergeMessages(data.messages)
      setStats({ total: data.total, buffered: data.buffered, lastId: data.lastId })
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    }
  }, [mergeMessages])

  const clear = useCallback(async () => {
    await clearMessages()
    setMessages([])
    // No se reinicia lastIdRef: los ids del servidor siguen avanzando.
  }, [])

  useEffect(() => {
    void catchUp()

    const source = new EventSource('/api/test/stream')

    source.addEventListener('open', () => {
      setConnection('live')
      setError(null)
    })

    source.addEventListener('hello', (event) => {
      setConnection('live')
      const data = JSON.parse((event as MessageEvent<string>).data) as LiveStats
      setStats(data)
      // Puede haber llegado algo mientras el stream estaba caído.
      if (data.lastId > lastIdRef.current) void catchUp()
    })

    source.addEventListener('message', (event) => {
      const message = JSON.parse((event as MessageEvent<string>).data) as RawMessage
      mergeMessages([message])
      setStats((current) => ({
        total: current.total + 1,
        buffered: Math.min(current.buffered + 1, MAX_CLIENT_MESSAGES),
        lastId: message.id,
      }))
    })

    source.addEventListener('cleared', () => {
      setMessages([])
      setStats((current) => ({ ...current, buffered: 0 }))
    })

    source.addEventListener('error', () => {
      // EventSource reintenta solo; sólo hay que reflejarlo en la interfaz.
      setConnection(source.readyState === EventSource.CLOSED ? 'offline' : 'connecting')
    })

    return () => source.close()
  }, [catchUp, mergeMessages])

  return { messages, stats, connection, error, clear, refresh: catchUp }
}
